import { useState, useEffect, useCallback, useMemo } from 'react';
import { X } from 'lucide-react';
import { entitiesApi } from '../../api/client';

// ── Design tokens ─────────────────────────────────────────────────────────
const C = {
  ivory: '#FAF7F2', cream: '#F3EDE4', parchment: '#EBE3D6', linen: '#E2D9CC',
  navy: '#1B2A4A', navyLight: '#2A3D66', navyMuted: '#3A5080',
  burgundy: '#7A2E3B', burgundyLight: '#944054',
  gold: '#B8953E', goldLight: '#D4AF5C',
  hunter: '#2D5F4A', hunterLight: '#3D7A60',
  ink: '#1A1A1A', textBody: '#3D3830', textMuted: '#7A7265', textLight: '#A69D91',
  border: '#D6CCBE', borderLight: '#E8E0D4',
};

const FONTS = {
  display: "'Cormorant Garamond', Georgia, serif",
  sans: "'Lato', 'Helvetica Neue', sans-serif",
  mono: "'IBM Plex Mono', 'Courier New', monospace",
};

// ── Owner color palette (matches pie chart on Entities tab) ───────────────
const OWNER_PALETTE = [
  C.navy,       // Partner A / first owner
  C.burgundy,   // Partner B / second owner
  C.hunter,     // Trust / third owner
  C.gold,       // Foundation / fourth owner
  '#6A5ACD',    // 5th+ owners get distinct hues
  '#CD853F',
  '#4682B4',
  '#8B4513',
];

// ── Entity type styling ───────────────────────────────────────────────────
const TYPE_COLORS: Record<string, { bg: string; fg: string; border: string }> = {
  family_lp:              { bg: '#F0E8D4', fg: C.gold,     border: C.gold },
  family_llc:             { bg: '#F0E8D4', fg: C.gold,     border: C.gold },
  s_corp:                 { bg: '#E0F0E8', fg: C.hunter,   border: C.hunter },
  c_corp:                 { bg: '#E0F0E8', fg: C.hunter,   border: C.hunter },
  revocable_trust:        { bg: '#F5E0E3', fg: C.burgundy, border: C.burgundy },
  irrevocable_trust:      { bg: '#F5E0E3', fg: C.burgundy, border: C.burgundy },
  grat:                   { bg: '#F5E0E3', fg: C.burgundy, border: C.burgundy },
  idgt:                   { bg: '#F5E0E3', fg: C.burgundy, border: C.burgundy },
  ilit:                   { bg: '#F5E0E3', fg: C.burgundy, border: C.burgundy },
  qtip_trust:             { bg: '#F5E0E3', fg: C.burgundy, border: C.burgundy },
  bypass_trust:           { bg: '#F5E0E3', fg: C.burgundy, border: C.burgundy },
  generation_skipping_trust: { bg: '#F5E0E3', fg: C.burgundy, border: C.burgundy },
  charitable_remainder_trust: { bg: '#F5E0E3', fg: C.burgundy, border: C.burgundy },
  foundation:             { bg: '#E0EAF5', fg: C.navy,     border: C.navy },
  donor_advised_fund:     { bg: '#E0EAF5', fg: C.navy,     border: C.navy },
  other:                  { bg: C.cream,   fg: C.textMuted, border: C.border },
};

const TYPE_LABELS: Record<string, string> = {
  family_lp: 'Family LP', family_llc: 'Family LLC', s_corp: 'S Corp', c_corp: 'C Corp',
  revocable_trust: 'Revocable Trust', irrevocable_trust: 'Irrevocable Trust',
  grat: 'GRAT', idgt: 'IDGT', ilit: 'ILIT', qtip_trust: 'QTIP Trust',
  bypass_trust: 'Bypass Trust', generation_skipping_trust: 'GST Trust',
  charitable_remainder_trust: 'CRT', foundation: 'Foundation',
  donor_advised_fund: 'DAF', other: 'Other',
};

function fmtCurrency(val: number | string | null | undefined): string {
  if (val == null) return '$0';
  const n = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(n) || n === 0) return '$0';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

// ── Types ─────────────────────────────────────────────────────────────────
interface MapEntity {
  id: string; name: string; entity_type: string;
  total_assets: number | string; domicile_state: string | null;
}
interface MapStake {
  entity_id: string; owner_name: string | null; ownership_pct: string | number;
  member_id: string | null; owner_entity_id: string | null; member_name: string | null;
}

interface LayoutNode {
  id: string; label: string; shortLabel: string;
  type: 'person' | 'entity';
  entityType?: string; totalAssets?: number;
  tier: number; x: number; y: number; w: number; h: number;
}
interface LayoutEdge {
  fromId: string; toId: string; pct: number; label: string;
  color: string; // assigned by owner
}

// ── Layout engine ─────────────────────────────────────────────────────────
const ENTITY_W = 200;
const ENTITY_H = 72;
const PERSON_W = 150;
const PERSON_H = 44;
const H_GAP = 40;
const V_GAP = 120;
const PADDING = 50;

function shortenName(name: string, maxLen = 22): string {
  if (name.length <= maxLen) return name;
  // Try to shorten by removing common suffixes
  const shortened = name
    .replace(/, (LP|LLC|Inc\.)$/, '')
    .replace(/ (LP|LLC|Inc\.)$/, '')
    .replace(/Family /, 'Fam. ');
  if (shortened.length <= maxLen) return shortened;
  return shortened.slice(0, maxLen - 1) + '…';
}

function computeLayout(entities: MapEntity[], stakes: MapStake[]): {
  nodes: LayoutNode[]; edges: LayoutEdge[]; width: number; height: number;
} {
  const nodes: LayoutNode[] = [];
  const edges: LayoutEdge[] = [];
  const entityIds = new Set(entities.map(e => e.id));

  // 1. Extract unique person owners
  const personMap = new Map<string, { label: string; memberId: string | null }>();
  for (const s of stakes) {
    const personLabel = s.member_name || s.owner_name || 'Unknown';
    if (s.owner_entity_id && entityIds.has(s.owner_entity_id)) continue;
    const matchesEntity = entities.some(e => e.name === s.owner_name || e.name === personLabel);
    if (matchesEntity && !s.member_id) continue;
    const key = s.member_id || `name:${personLabel}`;
    if (!personMap.has(key)) {
      personMap.set(key, { label: personLabel, memberId: s.member_id });
    }
  }

  const personKeys = Array.from(personMap.keys());
  for (const key of personKeys) {
    const p = personMap.get(key)!;
    nodes.push({
      id: `person:${key}`, label: p.label, shortLabel: shortenName(p.label, 18),
      type: 'person', tier: 0, x: 0, y: 0, w: PERSON_W, h: PERSON_H,
    });
  }

  // 2. Build ownership graph for topological tier assignment
  //    An entity's tier = 1 + max tier of all its owners
  //    Entities with NO owners are tier 0 (same level as people)
  const entityOwnerIds = new Map<string, Set<string>>(); // entity_id -> set of owner node ids
  for (const e of entities) entityOwnerIds.set(e.id, new Set());

  for (const s of stakes) {
    if (s.owner_entity_id && entityIds.has(s.owner_entity_id)) {
      entityOwnerIds.get(s.entity_id)?.add(`entity:${s.owner_entity_id}`);
    } else {
      const personLabel = s.member_name || s.owner_name || 'Unknown';
      const key = s.member_id || `name:${personLabel}`;
      entityOwnerIds.get(s.entity_id)?.add(`person:${key}`);
    }
  }

  // Compute tiers via BFS from roots
  const entityTiers: Record<string, number> = {};

  // Entities with zero owners go to tier 0 (peer with people)
  // Entities owned ONLY by people go to tier 1
  // Entities owned by tier-N entities go to tier N+1
  function getEntityTier(entityId: string, visited: Set<string> = new Set()): number {
    if (entityTiers[entityId] !== undefined) return entityTiers[entityId];
    if (visited.has(entityId)) return 1; // cycle protection
    visited.add(entityId);

    const owners = entityOwnerIds.get(entityId);
    if (!owners || owners.size === 0) {
      entityTiers[entityId] = 0; // no owners = root level
      return 0;
    }

    let maxOwnerTier = -1;
    for (const ownerId of owners) {
      if (ownerId.startsWith('person:')) {
        maxOwnerTier = Math.max(maxOwnerTier, 0);
      } else {
        const ownerEntityId = ownerId.replace('entity:', '');
        const ownerTier = getEntityTier(ownerEntityId, visited);
        maxOwnerTier = Math.max(maxOwnerTier, ownerTier);
      }
    }

    const tier = maxOwnerTier + 1;
    entityTiers[entityId] = tier;
    return tier;
  }

  for (const e of entities) getEntityTier(e.id);

  // Create entity nodes
  for (const e of entities) {
    const totalAssets = typeof e.total_assets === 'string' ? parseFloat(e.total_assets) : (e.total_assets || 0);
    nodes.push({
      id: `entity:${e.id}`, label: e.name, shortLabel: shortenName(e.name),
      type: 'entity', entityType: e.entity_type, totalAssets,
      tier: entityTiers[e.id] ?? 1,
      x: 0, y: 0, w: ENTITY_W, h: ENTITY_H,
    });
  }

  // Person color map for edge coloring
  const personColorMap = new Map<string, string>();
  personKeys.forEach((key, i) => {
    personColorMap.set(`person:${key}`, OWNER_PALETTE[i % OWNER_PALETTE.length]);
  });

  function getOwnerColor(fromId: string): string {
    if (fromId.startsWith('person:')) return personColorMap.get(fromId) || OWNER_PALETTE[0];
    // Entity owner — find the root person color
    const entityId = fromId.replace('entity:', '');
    const entityStakes = stakes.filter(s => s.entity_id === entityId);
    for (const s of entityStakes) {
      if (!s.owner_entity_id || !entityIds.has(s.owner_entity_id)) {
        const key = s.member_id || `name:${s.member_name || s.owner_name || 'Unknown'}`;
        const color = personColorMap.get(`person:${key}`);
        if (color) return color;
      }
    }
    return OWNER_PALETTE[0];
  }

  // 3. Create edges
  for (const s of stakes) {
    const pct = typeof s.ownership_pct === 'string' ? parseFloat(s.ownership_pct) : s.ownership_pct;
    if (!pct) continue;

    let fromId: string;
    if (s.owner_entity_id && entityIds.has(s.owner_entity_id)) {
      fromId = `entity:${s.owner_entity_id}`;
    } else {
      const personLabel = s.member_name || s.owner_name || 'Unknown';
      const key = s.member_id || `name:${personLabel}`;
      fromId = `person:${key}`;
    }
    const toId = `entity:${s.entity_id}`;

    edges.push({
      fromId, toId, pct,
      label: `${pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(1)}%`,
      color: (() => {
        const targetEntity = entities.find(e => `entity:${e.id}` === toId);
        if (targetEntity) {
          const tc = TYPE_COLORS[targetEntity.entity_type] || TYPE_COLORS.other;
          return tc.border;
        }
        return getOwnerColor(fromId);
      })(),
    });
  }

  // 4. Position nodes by tier
  const tiers: Record<number, LayoutNode[]> = {};
  for (const n of nodes) {
    if (!tiers[n.tier]) tiers[n.tier] = [];
    tiers[n.tier].push(n);
  }

  const tierKeys = Object.keys(tiers).map(Number).sort();
  let maxWidth = 0;

  for (const tier of tierKeys) {
    const tierNodes = tiers[tier];
    const tierWidth = tierNodes.reduce((sum, n) => sum + n.w, 0) + (tierNodes.length - 1) * H_GAP;
    if (tierWidth > maxWidth) maxWidth = tierWidth;
  }

  const totalWidth = Math.max(maxWidth + PADDING * 2, 700);

  let currentY = PADDING;
  for (const tier of tierKeys) {
    const tierNodes = tiers[tier];
    const tierWidth = tierNodes.reduce((sum, n) => sum + n.w, 0) + (tierNodes.length - 1) * H_GAP;
    let currentX = (totalWidth - tierWidth) / 2;
    const maxH = Math.max(...tierNodes.map(n => n.h));

    for (const node of tierNodes) {
      node.x = currentX;
      node.y = currentY + (maxH - node.h) / 2;
      currentX += node.w + H_GAP;
    }
    currentY += maxH + V_GAP;
  }

  const totalHeight = currentY - V_GAP + PADDING;

  return { nodes, edges, width: totalWidth, height: totalHeight };
}

// ── SVG Components ────────────────────────────────────────────────────────

function PersonNode({ node, highlighted, onHover }: {
  node: LayoutNode; highlighted: boolean;
  onHover: (id: string | null) => void;
}) {
  const opacity = highlighted ? 1 : 0.4;
  return (
    <g
      onMouseEnter={() => onHover(node.id)}
      onMouseLeave={() => onHover(null)}
      style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
      opacity={opacity}
    >
      <rect
        x={node.x} y={node.y} width={node.w} height={node.h}
        rx={22} ry={22}
        fill={C.navy} stroke={C.navyLight} strokeWidth={1.5}
      />
      <text
        x={node.x + node.w / 2} y={node.y + node.h / 2 + 1}
        textAnchor="middle" dominantBaseline="central"
        fill={C.ivory} fontFamily={FONTS.sans} fontSize={12} fontWeight={600}
      >
        {node.shortLabel}
      </text>
    </g>
  );
}

function EntityNode({ node, highlighted, onHover, onClick }: {
  node: LayoutNode; highlighted: boolean;
  onHover: (id: string | null) => void;
  onClick?: (id: string) => void;
}) {
  const tc = TYPE_COLORS[node.entityType || 'other'] || TYPE_COLORS.other;
  const typeLabel = TYPE_LABELS[node.entityType || 'other'] || node.entityType || 'Entity';
  const assets = fmtCurrency(node.totalAssets);
  const opacity = highlighted ? 1 : 0.35;

  return (
    <g
      onMouseEnter={() => onHover(node.id)}
      onMouseLeave={() => onHover(null)}
      onClick={() => onClick?.(node.id)}
      style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
      opacity={opacity}
    >
      {/* Card background */}
      <rect
        x={node.x} y={node.y} width={node.w} height={node.h}
        rx={4} ry={4}
        fill={C.ivory} stroke={tc.border} strokeWidth={1.5}
      />
      {/* Left accent bar */}
      <rect
        x={node.x} y={node.y} width={4} height={node.h}
        rx={2} ry={0}
        fill={tc.border}
      />
      {/* Entity name */}
      <text
        x={node.x + 14} y={node.y + 20}
        fill={C.ink} fontFamily={FONTS.sans} fontSize={12} fontWeight={700}
      >
        {node.shortLabel}
      </text>
      {/* Type label */}
      <text
        x={node.x + 14} y={node.y + 36}
        fill={tc.fg} fontFamily={FONTS.sans} fontSize={10} fontWeight={600}
        letterSpacing="0.04em"
      >
        {typeLabel}
      </text>
      {/* Assets */}
      <text
        x={node.x + 14} y={node.y + 54}
        fill={C.textMuted} fontFamily={FONTS.mono} fontSize={11}
      >
        {assets !== '$0' ? assets : '—'}
      </text>
      {/* State badge */}
      {node.type === 'entity' && (
        <text
          x={node.x + node.w - 12} y={node.y + 54}
          textAnchor="end"
          fill={C.textLight} fontFamily={FONTS.sans} fontSize={9} fontWeight={700}
          letterSpacing="0.06em"
        >
          {/* domicile_state would need to be on the node — skip for now */}
        </text>
      )}
    </g>
  );
}

// Edge path with simple spread and gentle curves
function OwnershipEdgePath({ edge, nodes, highlighted, allEdges, isHovering }: {
  edge: LayoutEdge; nodes: LayoutNode[]; highlighted: boolean; allEdges: LayoutEdge[]; isHovering: boolean;
}) {
  const from = nodes.find(n => n.id === edge.fromId);
  const to = nodes.find(n => n.id === edge.toId);
  if (!from || !to) return null;

  // Spread exit points along source bottom
  const edgesFromSource = allEdges.filter(e => e.fromId === edge.fromId);
  const srcIdx = edgesFromSource.indexOf(edge);
  const srcCount = edgesFromSource.length;
  const srcSpread = Math.min(from.w * 0.6, srcCount * 24);
  const srcStart = from.x + from.w / 2 - srcSpread / 2;
  const x1 = srcCount > 1 ? srcStart + (srcSpread / Math.max(1, srcCount - 1)) * srcIdx : from.x + from.w / 2;
  const y1 = from.y + from.h;

  // Spread entry points along target top
  const edgesToTarget = allEdges.filter(e => e.toId === edge.toId);
  const tgtIdx = edgesToTarget.indexOf(edge);
  const tgtCount = edgesToTarget.length;
  const tgtSpread = Math.min(to.w * 0.6, tgtCount * 24);
  const tgtStart = to.x + to.w / 2 - tgtSpread / 2;
  const x2 = tgtCount > 1 ? tgtStart + (tgtSpread / Math.max(1, tgtCount - 1)) * tgtIdx : to.x + to.w / 2;
  const y2 = to.y;

  // Simple cubic bezier — control points straight down/up
  const dy = y2 - y1;
  const path = `M ${x1} ${y1} C ${x1} ${y1 + dy * 0.4}, ${x2} ${y2 - dy * 0.4}, ${x2} ${y2}`;
  const opacity = !isHovering ? 0.6 : highlighted ? 1 : 0.12;
  const color = !isHovering ? edge.color : highlighted ? edge.color : C.border;
  const strokeW = !isHovering ? Math.max(1.5, Math.min(3, edge.pct / 30)) : highlighted ? Math.max(1.5, Math.min(3.5, edge.pct / 30)) : 1;

  return (
    <g style={{ transition: 'opacity 0.2s' }} opacity={opacity}>
      <path d={path} fill="none" stroke={color} strokeWidth={strokeW} />
      <polygon points={`${x2},${y2} ${x2-4},${y2-7} ${x2+4},${y2-7}`} fill={color} />
    </g>
  );
}

// Edge label — positioned on the actual bezier curve path
function OwnershipEdgeLabel({ edge, nodes, highlighted, labelIndex: _labelIndex, totalLabelsAtTarget: _totalLabelsAtTarget, allEdges }: {
  edge: LayoutEdge; nodes: LayoutNode[]; highlighted: boolean;
  labelIndex: number; totalLabelsAtTarget: number; allEdges: LayoutEdge[];
}) {
  const from = nodes.find(n => n.id === edge.fromId);
  const to = nodes.find(n => n.id === edge.toId);
  if (!from || !to) return null;

  // Same spread math as OwnershipEdgePath
  const edgesFromSource = allEdges.filter(e => e.fromId === edge.fromId);
  const srcIdx = edgesFromSource.indexOf(edge);
  const srcCount = edgesFromSource.length;
  const srcSpread = Math.min(from.w * 0.6, srcCount * 24);
  const srcStart = from.x + from.w / 2 - srcSpread / 2;
  const x1 = srcCount > 1 ? srcStart + (srcSpread / Math.max(1, srcCount - 1)) * srcIdx : from.x + from.w / 2;
  const y1 = from.y + from.h;

  const edgesToTarget = allEdges.filter(e => e.toId === edge.toId);
  const tgtIdx = edgesToTarget.indexOf(edge);
  const tgtCount = edgesToTarget.length;
  const tgtSpread = Math.min(to.w * 0.6, tgtCount * 24);
  const tgtStart = to.x + to.w / 2 - tgtSpread / 2;
  const x2 = tgtCount > 1 ? tgtStart + (tgtSpread / Math.max(1, tgtCount - 1)) * tgtIdx : to.x + to.w / 2;
  const y2 = to.y;

  // Sample bezier at t=0.5 (midpoint)
  const dy = y2 - y1;
  const cp1y = y1 + dy * 0.4;
  const cp2y = y2 - dy * 0.4;
  const t = 0.5;
  const mt = 1 - t;
  const labelX = mt*mt*mt*x1 + 3*mt*mt*t*x1 + 3*mt*t*t*x2 + t*t*t*x2;
  const labelY = mt*mt*mt*y1 + 3*mt*mt*t*cp1y + 3*mt*t*t*cp2y + t*t*t*y2;

  // Only show labels on hover — keeps the map clean
  if (!highlighted) return null;

  return (
    <g style={{ transition: 'opacity 0.15s' }}>
      <rect
        x={labelX - 24} y={labelY - 10} width={48} height={18}
        rx={9} ry={9} fill={C.ivory} stroke={edge.color} strokeWidth={1.5}
        style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.12))' }}
      />
      <text
        x={labelX} y={labelY + 1}
        textAnchor="middle" dominantBaseline="central"
        fill={edge.color}
        fontFamily={FONTS.mono} fontSize={10} fontWeight={700}
      >
        {edge.label}
      </text>
    </g>
  );
}

// ── Main Component ────────────────────────────────────────────────────────

export default function OwnershipMap() {
  const [entities, setEntities] = useState<MapEntity[]>([]);
  const [stakes, setStakes] = useState<MapStake[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [zoom, setZoom] = useState(0.85);
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [summaryData, setSummaryData] = useState<any>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const zoomIn = () => setZoom(z => Math.min(z + 0.15, 2));
  const zoomOut = () => setZoom(z => Math.max(z - 0.15, 0.3));
  const zoomReset = () => setZoom(0.85);
  const handleEntityClick = useCallback(async (nodeId: string) => {
    const entityId = nodeId.replace('entity:', '');
    if (selectedEntityId === entityId) { setSelectedEntityId(null); setSummaryData(null); return; }
    setSelectedEntityId(entityId);
    setSummaryLoading(true);
    try {
      const res = await entitiesApi.getSummary(entityId);
      setSummaryData(res.data.data || null);
    } catch { setSummaryData(null); }
    finally { setSummaryLoading(false); }
  }, [selectedEntityId]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await entitiesApi.ownershipMap();
      setEntities(res.data.entities || []);
      setStakes(res.data.stakes || []);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load ownership data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const { nodes, edges, width, height } = useMemo(
    () => computeLayout(entities, stakes),
    [entities, stakes]
  );

  // Determine which nodes/edges are highlighted
  const highlightedNodeIds = useMemo(() => {
    if (!hoveredNode) return new Set(nodes.map(n => n.id)); // all nodes visible when nothing hovered
    const ids = new Set<string>([hoveredNode]);
    for (const e of edges) {
      if (e.fromId === hoveredNode || e.toId === hoveredNode) {
        ids.add(e.fromId);
        ids.add(e.toId);
      }
    }
    return ids;
  }, [hoveredNode, nodes, edges]);

  const highlightedEdges = useMemo(() => {
    if (!hoveredNode) return new Set<number>();
    const idxs = new Set<number>();
    edges.forEach((e, i) => {
      if (e.fromId === hoveredNode || e.toId === hoveredNode) idxs.add(i);
    });
    return idxs;
  }, [hoveredNode, edges]);

  if (loading) {
    return (
      <div style={{
        background: C.ivory, border: `1px solid ${C.border}`, borderRadius: 3,
        padding: '48px 24px', textAlign: 'center', marginBottom: 24,
      }}>
        <span style={{ fontFamily: FONTS.sans, fontSize: 13, color: C.textMuted }}>
          Loading ownership structure…
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        background: 'rgba(122,46,59,0.08)', border: `1px solid ${C.burgundy}`,
        borderRadius: 3, padding: '14px 18px', marginBottom: 24,
      }}>
        <span style={{ fontFamily: FONTS.sans, fontSize: 13, color: C.burgundy }}>{error}</span>
      </div>
    );
  }

  if (entities.length === 0) {
    return (
      <div style={{
        background: C.ivory, border: `1px solid ${C.border}`, borderRadius: 3,
        padding: '48px 24px', textAlign: 'center', marginBottom: 24,
      }}>
        <div style={{ fontFamily: FONTS.display, fontSize: 36, color: C.textLight, marginBottom: 8 }}>⊘</div>
        <span style={{ fontFamily: FONTS.sans, fontSize: 14, color: C.textMuted, fontWeight: 600 }}>
          No entities to map
        </span>
        <p style={{ fontFamily: FONTS.sans, fontSize: 12, color: C.textLight, marginTop: 4 }}>
          Add entities above, then define ownership stakes to see the structure.
        </p>
      </div>
    );
  }

  const hasStakes = stakes.length > 0;

  return (
    <div style={{
      background: C.ivory, border: `1px solid ${C.border}`, borderRadius: 3,
      marginBottom: 24, overflow: 'hidden', position: 'relative',
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 24px 16px',
        borderBottom: `1px solid ${C.borderLight}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div>
          <h2 style={{
            fontSize: 13, fontWeight: 700, color: C.textMuted, fontFamily: FONTS.sans,
            letterSpacing: '0.1em', textTransform: 'uppercase', margin: 0,
            borderBottom: `2px solid ${C.navy}`, display: 'inline-block', paddingBottom: 4,
          }}>Ownership Structure</h2>
          <p style={{ fontSize: 13, color: C.textLight, margin: '6px 0 0', fontFamily: FONTS.sans, fontStyle: 'italic' }}>
            {hasStakes
              ? 'Hover over any node to see ownership percentages'
              : 'Add ownership stakes to entities to see the ownership hierarchy'
            }
          </p>
        </div>
        {/* Zoom Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginRight: 16 }}>
          <button onClick={zoomOut} style={{
            width: 28, height: 28, border: `1px solid ${C.border}`, borderRadius: 3,
            background: C.cream, color: C.textBody, fontFamily: FONTS.sans, fontSize: 16,
            fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center',
            justifyContent: 'center', lineHeight: 1,
          }}>−</button>
          <button onClick={zoomReset} style={{
            height: 28, border: `1px solid ${C.border}`, borderRadius: 3, padding: '0 8px',
            background: C.cream, color: C.textMuted, fontFamily: FONTS.mono, fontSize: 10,
            fontWeight: 600, cursor: 'pointer', letterSpacing: '0.02em',
          }}>{Math.round(zoom * 100)}%</button>
          <button onClick={zoomIn} style={{
            width: 28, height: 28, border: `1px solid ${C.border}`, borderRadius: 3,
            background: C.cream, color: C.textBody, fontFamily: FONTS.sans, fontSize: 16,
            fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center',
            justifyContent: 'center', lineHeight: 1,
          }}>+</button>
        </div>
        {/* Legend */}
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          {[
            { label: 'Person', color: C.navy },
            { label: 'LP / LLC', color: C.gold },
            { label: 'Trust', color: C.burgundy },
            { label: 'Corp', color: C.hunter },
            { label: 'Foundation', color: C.navy },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{
                width: i === 0 ? 18 : 12, height: i === 0 ? 12 : 12,
                borderRadius: i === 0 ? 6 : 2,
                background: i === 0 ? item.color : 'transparent',
                border: i === 0 ? 'none' : `2px solid ${item.color}`,
              }} />
              <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: C.textLight, fontWeight: 600 }}>
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* SVG Map */}
      <div style={{ overflow: 'auto', padding: '0', maxHeight: '70vh' }}>
        <div style={{
          transform: `scale(${zoom})`, transformOrigin: 'top left',
          width: width, height: height,
        }}>
        <svg
          width="100%" height={height}
          viewBox={`0 0 ${width} ${height}`}
          style={{ display: 'block', minWidth: width }}
        >
          {/* Background */}
          <rect x={0} y={0} width={width} height={height} fill={C.ivory} />

          {/* Layer 1: Edge paths (bottom — behind everything) */}
          <g>
            {edges.map((edge, i) => (
              <OwnershipEdgePath
                key={`path-${i}`} edge={edge} nodes={nodes}
                highlighted={highlightedEdges.has(i)}
                allEdges={edges}
                isHovering={hoveredNode !== null}
              />
            ))}
          </g>

          {/* Layer 2: Nodes (middle — on top of edge paths) */}
          <g>
            {nodes.map(node => (
              node.type === 'person' ? (
                <PersonNode
                  key={node.id} node={node}
                  highlighted={highlightedNodeIds.has(node.id)}
                  onHover={setHoveredNode}
                />
              ) : (
                <EntityNode
                  key={node.id} node={node}
                  highlighted={highlightedNodeIds.has(node.id)}
                  onHover={setHoveredNode}
                  onClick={handleEntityClick}
                />
              )
            ))}
          </g>

          {/* Layer 3: Edge labels (top — always visible above nodes) */}
          <g>
            {edges.map((edge, i) => {
              const sameTarget = edges.filter(e => e.toId === edge.toId);
              const labelIdx = sameTarget.indexOf(edge);
              return (
                <OwnershipEdgeLabel
                  key={`label-${i}`} edge={edge} nodes={nodes}
                  highlighted={highlightedEdges.has(i)}
                  labelIndex={labelIdx}
                  totalLabelsAtTarget={sameTarget.length}
                  allEdges={edges}
                />
              );
            })}
          </g>
        </svg>
      </div>
      </div>
      {/* ── Entity Summary Panel ──────────────────────── */}
      {selectedEntityId && (
        <div style={{
          position: 'absolute', top: 0, right: 0, width: 360, height: '100%',
          background: C.ivory, borderLeft: `1px solid ${C.border}`,
          boxShadow: '-4px 0 12px rgba(0,0,0,0.08)', overflowY: 'auto',
          zIndex: 10,
        }}>
          {summaryLoading ? (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <span style={{ fontFamily: FONTS.sans, fontSize: 13, color: C.textMuted }}>Loading...</span>
            </div>
          ) : summaryData ? (
            <div style={{ padding: 0 }}>
              {/* Header */}
              <div style={{
                padding: '16px 20px', borderBottom: `1px solid ${C.borderLight}`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                borderLeft: `4px solid ${(TYPE_COLORS[summaryData.entity.entity_type] || TYPE_COLORS.other).border}`,
              }}>
                <div>
                  <h3 style={{ fontFamily: FONTS.display, fontSize: 18, color: C.ink, margin: 0 }}>{summaryData.entity.name}</h3>
                  <span style={{ fontFamily: FONTS.sans, fontSize: 11, color: (TYPE_COLORS[summaryData.entity.entity_type] || TYPE_COLORS.other).fg, fontWeight: 600, letterSpacing: '0.04em' }}>
                    {TYPE_LABELS[summaryData.entity.entity_type] || summaryData.entity.entity_type}
                  </span>
                  {summaryData.entity.tax_form && summaryData.entity.tax_form !== 'none' && (
                    <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: C.textLight, marginLeft: 8 }}>Form {summaryData.entity.tax_form}</span>
                  )}
                  {summaryData.entity.ein && (
                    <p style={{ fontFamily: FONTS.mono, fontSize: 10, color: C.textLight, margin: '4px 0 0' }}>EIN: {summaryData.entity.ein}</p>
                  )}
                </div>
                <button onClick={() => { setSelectedEntityId(null); setSummaryData(null); }} style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                  color: C.textLight, borderRadius: 3,
                }}>
                  <X size={16} />
                </button>
              </div>

              {/* Net Position */}
              <div style={{
                padding: '12px 20px', borderBottom: `1px solid ${C.borderLight}`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{ fontFamily: FONTS.sans, fontSize: 11, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Net Position</span>
                <span style={{
                  fontFamily: FONTS.mono, fontSize: 16, fontWeight: 700,
                  color: summaryData.netPosition >= 0 ? C.hunter : C.burgundy,
                }}>{fmtCurrency(summaryData.netPosition)}</span>
              </div>

              {/* Ownership */}
              {summaryData.ownership.length > 0 && (
                <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.borderLight}` }}>
                  <p style={{ fontFamily: FONTS.sans, fontSize: 10, color: C.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Ownership</p>
                  {summaryData.ownership.map((o: any, i: number) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                      <span style={{ fontFamily: FONTS.sans, fontSize: 12, color: C.textBody }}>{o.member_name || o.owner_entity_name || o.owner_name}</span>
                      <span style={{ fontFamily: FONTS.mono, fontSize: 12, color: C.navy, fontWeight: 600 }}>{parseFloat(o.ownership_pct).toFixed(1)}%</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Assets */}
              <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.borderLight}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <p style={{ fontFamily: FONTS.sans, fontSize: 10, color: C.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>Assets ({summaryData.assets.count})</p>
                  <span style={{ fontFamily: FONTS.mono, fontSize: 13, color: C.navy, fontWeight: 700 }}>{fmtCurrency(summaryData.assets.total)}</span>
                </div>
                {summaryData.assets.items.map((a: any) => (
                  <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                    <span style={{ fontFamily: FONTS.sans, fontSize: 11, color: C.textBody, maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
                    <span style={{ fontFamily: FONTS.mono, fontSize: 11, color: C.textMuted }}>{fmtCurrency(a.current_value)}</span>
                  </div>
                ))}
              </div>

              {/* Liabilities */}
              <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.borderLight}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <p style={{ fontFamily: FONTS.sans, fontSize: 10, color: C.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>Liabilities ({summaryData.liabilities.count})</p>
                  <span style={{ fontFamily: FONTS.mono, fontSize: 13, color: C.burgundy, fontWeight: 700 }}>{fmtCurrency(summaryData.liabilities.total)}</span>
                </div>
                {summaryData.liabilities.items.map((l: any) => (
                  <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                    <span style={{ fontFamily: FONTS.sans, fontSize: 11, color: C.textBody, maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</span>
                    <span style={{ fontFamily: FONTS.mono, fontSize: 11, color: C.burgundy }}>{fmtCurrency(l.outstanding_balance)}</span>
                  </div>
                ))}
                {summaryData.liabilities.annualDebtService > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0 0', borderTop: `1px solid ${C.borderLight}`, marginTop: 4 }}>
                    <span style={{ fontFamily: FONTS.sans, fontSize: 11, color: C.textMuted, fontWeight: 600 }}>Annual Debt Service</span>
                    <span style={{ fontFamily: FONTS.mono, fontSize: 11, color: C.gold, fontWeight: 600 }}>{fmtCurrency(summaryData.liabilities.annualDebtService)}</span>
                  </div>
                )}
              </div>

              {/* State / Formation */}
              {(summaryData.entity.domicile_state || summaryData.entity.formation_date) && (
                <div style={{ padding: '14px 20px' }}>
                  <p style={{ fontFamily: FONTS.sans, fontSize: 10, color: C.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Details</p>
                  {summaryData.entity.domicile_state && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                      <span style={{ fontFamily: FONTS.sans, fontSize: 11, color: C.textMuted }}>Domicile</span>
                      <span style={{ fontFamily: FONTS.sans, fontSize: 11, color: C.textBody, fontWeight: 600 }}>{summaryData.entity.domicile_state}</span>
                    </div>
                  )}
                  {summaryData.entity.formation_date && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                      <span style={{ fontFamily: FONTS.sans, fontSize: 11, color: C.textMuted }}>Formed</span>
                      <span style={{ fontFamily: FONTS.sans, fontSize: 11, color: C.textBody }}>{new Date(summaryData.entity.formation_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
                    </div>
                  )}
                  {summaryData.entity.is_operating_business && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                      <span style={{ fontFamily: FONTS.sans, fontSize: 11, color: C.textMuted }}>Operating Business</span>
                      <span style={{ fontFamily: FONTS.sans, fontSize: 11, color: C.hunter, fontWeight: 600 }}>Yes</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <span style={{ fontFamily: FONTS.sans, fontSize: 13, color: C.textMuted }}>No data available</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
